import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Scale } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  serviceApi,
  onPath,
  onKeys,
  unwrap,
  useTypedConfig,
  type LoadBalancer,
  type ConfigFlavor,
} from '@/api/opennebula';
import { lbFlavors } from './lb-flavors';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Resize a load balancer to a different flavor. OpenNebula cold-resizes the
 * VRouter VM, so the flow powers it off first, applies the new flavor, then
 * resumes if it was running — the LB is briefly offline during the swap.
 * Rendered as a modal controlled by the detail page (mount = open).
 */
export function LoadBalancerResize({
  serviceId,
  lb,
  onClose,
  onResized,
}: {
  serviceId: string;
  lb: LoadBalancer;
  onClose: () => void;
  /** Called with the new flavor after a successful resize+resume — used by the
   *  Kubernetes ingress console to persist the desired flavor onto the cluster. */
  onResized?: (flavor: string) => Promise<void> | void;
}) {
  const { show } = useToast();
  const qc = useQueryClient();
  const { data: config } = useTypedConfig(serviceId, 'load-balancer');

  const flavors = config?.flavors ?? [];
  const visible = useMemo(() => lbFlavors(flavors), [flavors]);

  // Current flavor — older LBs may not carry `metadata.flavor`; fall back to
  // matching the flavor whose cores+memory equal the live resources.
  const current =
    lb.metadata?.flavor ??
    flavors.find(
      (f) => f.cores === lb.metadata?.resources?.cores && f.memory === lb.metadata?.resources?.memory,
    )?.name;

  const [flavor, setFlavor] = useState<string>(current ?? '');
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  // Status casing differs by source: the s10003 detail returns POWERED_ON/POWERED_OFF
  // while the s10015 ingress feed returns the raw lowercase state (active/powered_off),
  // so compare case-insensitively — otherwise the resume step below is skipped and the
  // LB is left powered off after a resize.
  const statusUp = (lb.status ?? '').toUpperCase();
  const isOff = statusUp === 'POWERED_OFF';
  const needsPowerOff = !isOff;

  async function waitForPowerOff(maxAttempts = 60, interval = 3000): Promise<boolean> {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const body = unwrap<{ instance?: { status?: string } }>(
          await serviceApi.get(onPath(serviceId, `lbs/${lb.id}`)),
        );
        if ((body.instance?.status ?? '').toUpperCase() === 'POWERED_OFF') return true;
      } catch {
        /* keep polling */
      }
      await sleep(interval);
    }
    return false;
  }

  async function resize() {
    if (!flavor || flavor === current) return;
    setBusy(true);
    // Resume after resizing unless the LB was already off/terminated — covers both
    // 'POWERED_ON' (s10003) and 'active' (s10015 ingress) running states.
    const wasRunning = !isOff && statusUp !== 'TERMINATED';
    try {
      if (needsPowerOff) {
        setStatusMsg('Powering off to apply the new configuration…');
        await serviceApi.post(onPath(serviceId, `lbs/${lb.id}/perform`), { perform: 'poweroff' });
        const off = await waitForPowerOff();
        if (!off) throw new Error('Timed out waiting for the load balancer to power off.');
      }
      setStatusMsg('Applying the new size…');
      await serviceApi.post(onPath(serviceId, `lbs/${lb.id}/resize`), { flavor });
      if (wasRunning) {
        setStatusMsg('Restarting the load balancer…');
        await serviceApi.post(onPath(serviceId, `lbs/${lb.id}/perform`), { perform: 'resume' });
      }
      if (onResized) {
        setStatusMsg('Saving the new size to the cluster…');
        await onResized(flavor);
      }
      qc.invalidateQueries({ queryKey: onKeys.lb(serviceId, lb.id) });
      qc.invalidateQueries({ queryKey: onKeys.lbs(serviceId) });
      show({ tone: 'success', title: 'Load balancer resized' });
      onClose();
    } catch (e) {
      show({ tone: 'danger', title: (e as Error)?.message || 'Resize failed' });
    } finally {
      setBusy(false);
      setStatusMsg('');
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !busy) onClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Resize load balancer</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            Current type: <b>{current || '—'}</b>. Pick a new size below.
          </p>

          {visible.length === 0 && (
            <Alert tone="info" style={{ marginTop: 'var(--vn-space-3)' }}>
              No sizes are available.
            </Alert>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 'var(--vn-space-2)',
              marginTop: 'var(--vn-space-3)',
            }}
          >
            {visible.map((f: ConfigFlavor) => {
              const sel = f.name === flavor;
              return (
                <button
                  key={f.name}
                  type="button"
                  disabled={f.disabled || busy}
                  onClick={() => setFlavor(f.name)}
                  style={{
                    display: 'grid',
                    gap: 2,
                    textAlign: 'left',
                    cursor: f.disabled ? 'not-allowed' : 'pointer',
                    font: 'inherit',
                    color: f.disabled ? 'var(--vn-fg-subtle)' : 'var(--vn-fg-default)',
                    opacity: f.disabled ? 0.5 : 1,
                    border: `1px solid ${sel ? 'var(--vn-accent-solid)' : 'var(--vn-border-default)'}`,
                    outline: sel ? '1px solid var(--vn-accent-solid)' : 'none',
                    borderRadius: 'var(--vn-radius-md)',
                    background: 'var(--vn-bg-surface)',
                    padding: 'var(--vn-space-3)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{f.cores} vCPU</span>
                  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{f.memory} GB RAM</span>
                  <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-xs)' }}>
                    {f.name}
                    {f.name === current ? ' · current' : ''}
                  </span>
                </button>
              );
            })}
          </div>

          <Alert tone="warning" style={{ marginTop: 'var(--vn-space-4)' }}>
            <b>Important:</b> the load balancer {needsPowerOff ? 'will be powered off and restarted' : 'will be updated'} to
            apply the new configuration. Traffic is interrupted while it restarts; this typically takes 1–3 minutes.
          </Alert>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--vn-space-2)',
              marginTop: 'var(--vn-space-3)',
              fontSize: 'var(--vn-font-size-sm)',
            }}
          >
            <Checkbox checked={understood} onCheckedChange={(c) => setUnderstood(c === true)} disabled={busy} />
            I understand this {needsPowerOff ? 'will restart the load balancer and briefly interrupt traffic' : 'will apply the new configuration'}.
          </label>

          {busy && statusMsg && (
            <p style={{ marginBottom: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{statusMsg}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            startIcon={<Scale size={15} />}
            onClick={resize}
            disabled={busy || !understood || !flavor || flavor === current}
          >
            {busy ? 'Resizing…' : 'Proceed with resize'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
